<template>
  <div class="recharge-container">
    <div class="recharge-header">
      <h2>在线充值</h2>
      <p>选择充值金额，微信支付安全便捷</p>
    </div>

    <!-- 充值金额选择 -->
    <div class="amount-selection">
      <div 
        v-for="option in amountOptions" 
        :key="option.amount"
        :class="['amount-card', { active: selectedAmount === option.amount }]"
        @click="selectAmount(option.amount)"
      >
        <div class="amount-value">¥{{ (option.amount / 100).toFixed(2) }}</div>
        <div class="amount-bonus" v-if="option.bonus">
          {{ option.bonus }}积分
        </div>
        <div class="amount-label">{{ option.label }}</div>
      </div>
    </div>

    <!-- 自定义金额 -->
    <div class="custom-amount-section">
      <h3>自定义金额</h3>
      <div class="custom-input-wrapper">
        <span class="currency-symbol">¥</span>
        <el-input-number 
          v-model="customAmount" 
          :min="100" 
          :max="1000000" 
          :step="100"
          placeholder="请输入金额（单位：分）"
          controls-position="right"
        />
        <span class="amount-tip">最小充值金额：¥1.00</span>
      </div>
      <el-button 
        type="success" 
        class="custom-confirm-btn"
        @click="selectCustomAmount"
        :disabled="!isValidCustomAmount"
      >
        确认充值
      </el-button>
    </div>

    <!-- 支付弹窗 -->
    <el-dialog 
      v-model="paymentDialogVisible" 
      title="微信支付"
      width="400px"
      :close-on-click-modal="false"
      @close="handleClosePayment"
    >
      <div class="payment-content" v-loading="paymentLoading">
        <!-- 订单信息 -->
        <div class="order-info">
          <div class="info-row">
            <span class="label">订单号：</span>
            <span class="value">{{ currentOrderNo }}</span>
          </div>
          <div class="info-row">
            <span class="label">商品描述：</span>
            <span class="value">{{ paymentDescription }}</span>
          </div>
          <div class="info-row">
            <span class="label">支付金额：</span>
            <span class="value amount-highlight">¥{{ (paymentAmount / 100).toFixed(2) }}</span>
          </div>
          <div class="info-row">
            <span class="label">过期时间：</span>
            <span class="value">{{ expireTime }}</span>
          </div>
        </div>

        <!-- 二维码显示 -->
        <div class="qr-code-section" v-if="qrCodeUrl">
          <img :src="qrCodeUrl" alt="支付二维码" class="qr-code-image" />
          <p class="qr-tip">请使用微信扫码支付</p>
        </div>

        <!-- 支付状态 -->
        <div class="payment-status" v-if="paymentStatus">
          <el-alert 
            :title="statusMessage" 
            :type="statusType"
            :closable="false"
            show-icon
          />
        </div>

        <!-- 操作按钮 -->
        <div class="payment-actions">
          <el-button @click="handleClosePayment">取消支付</el-button>
          <el-button type="primary" @click="refreshPaymentStatus" :loading="polling">
            刷新状态
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 支付成功弹窗 -->
    <el-dialog 
      v-model="successDialogVisible" 
      title="支付成功"
      width="350px"
      :close-on-click-modal="false"
    >
      <div class="success-content">
        <div class="success-icon">✓</div>
        <h3>充值成功！</h3>
      </div>
      <template #footer>
        <el-button type="primary" @click="successDialogVisible = false">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { ElMessage } from 'element-plus';
import { createPayment, getPaymentStatus, closePayment } from '@/api/payment';
import { getCurrentUser } from '@/api/user';
import { useStore } from 'vuex';

const store = useStore();

// 充值金额选项（单位：分）
const amountOptions = [
  { amount: 100, label: '体验版', bonus: 0 },
  { amount: 500, label: '入门版', bonus: 50 },
  { amount: 1000, label: '标准版', bonus: 100 },
  { amount: 1500, label: '专业版', bonus: 150 },
  { amount: 3000, label: '旗舰版', bonus: 300 },
  { amount: 10000, label: '至尊版', bonus: 1000 }
];

// 状态管理
const selectedAmount = ref(null);
const customAmount = ref(null);
const paymentDialogVisible = ref(false);
const successDialogVisible = ref(false);
const paymentLoading = ref(false);
const polling = ref(false);
const qrCodeUrl = ref('');
const currentOrderNo = ref('');
const paymentDescription = ref('');
const paymentAmount = ref(0);
const expireTime = ref('');
const paymentStatus = ref('pending');
const paidTime = ref('');
const bonusPoints = ref(0);

// 轮询定时器
let pollTimer = null;

// 计算自定义金额是否有效
const isValidCustomAmount = computed(() => {
  return customAmount.value >= 100 && customAmount.value <= 1000000;
});

// 选择金额
const selectAmount = (amount) => {
  console.log(amount);
  
  // 检查是否登录
  if (!store.getters.isLoggedIn) {
    ElMessage.warning('请先登录后再充值');
    return;
  }
  
  selectedAmount.value = amount;
  customAmount.value = null;
  
  // 查找对应的赠送积分
  const option = amountOptions.find(opt => opt.amount === amount);
  bonusPoints.value = option?.bonus || 0;
  
  // 发起支付
  initiatePayment((amount / 100).toFixed(2) , option?.label || '自定义充值');
};

// 选择自定义金额
const selectCustomAmount = () => {
  // 检查是否登录
  if (!store.getters.isLoggedIn) {
    ElMessage.warning('请先登录后再充值');
    return;
  }
  
  if (!isValidCustomAmount.value) {
    ElMessage.warning('请输入有效的充值金额');
    return;
  }
  
  selectedAmount.value = null;
  bonusPoints.value = 0;
  initiatePayment(customAmount.value, '自定义金额充值');
};

// 发起支付
const initiatePayment = async (amount, label) => {
  paymentLoading.value = true;
  
  try {
    // 获取当前用户 ID
    let userId = store.state.userInfo?.id;
    
    // 如果没有用户信息，尝试从 API 获取
    if (!userId) {
      const userResponse = await getCurrentUser();
      if (userResponse.data.code === 200) {
       userId = userResponse.data.data.id;
      }
    }
    
    if (!userId) {
      throw new Error('未获取到用户 ID，请先登录');
    }
    
    const description = `在线充值 - ${label}`;
    const attach = `type:recharge`;
    
    const response = await createPayment({
     description,
     amount: amount,
     userId,
     attach,
      expireMinutes: 30
    });
    console.log(response,'responseresponseresponse');
    
    if (response.data.code === 200) {
      // 设置支付信息
      currentOrderNo.value = response.data.data.orderNo;
      paymentDescription.value = response.data.data.description;
      paymentAmount.value = response.data.data.amount;
      qrCodeUrl.value = response.data.data.qrCodeUrl;
      expireTime.value = formatDateTime(response.data.data.expireTime);
      paymentStatus.value = 'pending';
      paidTime.value = '';
      
      // 显示支付弹窗
      paymentDialogVisible.value = true;
      
      // 开始轮询支付状态
      startPolling(response.data.data.orderNo);
    } else {
      throw new Error(response.data.message || '创建订单失败');
    }
  } catch (error) {
    console.log(error);

    ElMessage.error(error.response?.data?.message || error.message || '创建订单失败');
  } finally {
    paymentLoading.value = false;
  }
};

// 开始轮询支付状态
const startPolling = (orderNo) => {
  polling.value = true;
  const maxAttempts = 600; // 最多轮询 30 分钟
  let attempts = 0;
  
  const poll = async () => {
    try {
      const response = await getPaymentStatus(orderNo);
      
      if (response.data.code === 200) {
        const { status, payAmount, paidTime: paidTimeStr } = response.data.data;
        
        paymentStatus.value = status;
        
        if (status === 'paid') {
          // 支付成功
          stopPolling();
          paidTime.value = formatDateTime(paidTimeStr);
          handlePaymentSuccess(payAmount);
        } else if (status === 'closed') {
          // 订单已关闭
          stopPolling();
          ElMessage.warning('订单已关闭，请重新下单');
          handleClosePayment();
        } else if (status === 'pending') {
          // 继续等待
          console.log('等待支付...');
        }
      }
    } catch (error) {
      console.error('轮询失败:', error);
    }
    
    attempts++;
    if (attempts >= maxAttempts) {
      stopPolling();
      ElMessage.info('支付超时，请重新下单');
      handleClosePayment();
    }
  };
  
  // 立即执行一次，然后每 3 秒轮询
  poll();
  pollTimer = setInterval(poll, 3000);
};

// 停止轮询
const stopPolling = () => {
  polling.value = false;
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

// 处理支付成功
const handlePaymentSuccess = async (payAmount) => {
  // 更新用户积分信息
  try {
    const userResponse = await getCurrentUser();
    if (userResponse.data.code === 200) {
      store.commit('SET_USER_INFO', userResponse.data.data);
    }
  } catch (error) {
    console.error('获取用户信息失败:', error);
  }
  
  // 关闭支付弹窗，显示成功弹窗
  paymentDialogVisible.value = false;
  successDialogVisible.value = true;
  
  ElMessage.success(`充值成功！支付金额：¥${(payAmount / 100).toFixed(2)}`);
};

// 刷新支付状态
const refreshPaymentStatus = async () => {
  if (!currentOrderNo.value) return;
  
  try {
    const response = await getPaymentStatus(currentOrderNo.value);
    
    if (response.data.code === 200) {
      const { status, payAmount, paidTime: paidTimeStr, qrCodeUrl: newQrCode } = response.data.data;
      
      paymentStatus.value = status;
      
      if (status === 'paid') {
        paidTime.value = formatDateTime(paidTimeStr);
        handlePaymentSuccess(payAmount);
      } else if (status === 'closed') {
        ElMessage.warning('订单已关闭，请重新下单');
        handleClosePayment();
      }
      
      if (newQrCode) {
        qrCodeUrl.value = newQrCode;
      }
    }
  } catch (error) {
    ElMessage.error('查询支付状态失败');
  }
};

// 关闭支付弹窗
const handleClosePayment = async () => {
  stopPolling();
  
  // 如果订单还在待支付状态，尝试关闭订单
  if (paymentStatus.value === 'pending' && currentOrderNo.value) {
    try {
      await closePayment(currentOrderNo.value);
    } catch (error) {
      console.error('关闭订单失败:', error);
    }
  }
  
  paymentDialogVisible.value = false;
  resetPaymentState();
};

// 重置支付状态
const resetPaymentState = () => {
  currentOrderNo.value = '';
  paymentDescription.value = '';
  paymentAmount.value = 0;
  qrCodeUrl.value = '';
  expireTime.value = '';
  paymentStatus.value = 'pending';
  paidTime.value = '';
  bonusPoints.value = 0;
  selectedAmount.value = null;
  customAmount.value = null;
};

// 格式化日期时间
const formatDateTime = (dateString) => {
  if (!dateString) return '未知';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '未知';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// 获取状态消息和类型
const statusMessage = computed(() => {
  switch (paymentStatus.value) {
    case 'pending':
      return '等待支付中...';
    case 'paid':
      return '支付成功！';
    case 'closed':
      return '订单已关闭';
    default:
      return '';
  }
});

const statusType = computed(() => {
  switch (paymentStatus.value) {
    case 'pending':
      return 'warning';
    case 'paid':
      return 'success';
    case 'closed':
      return 'error';
    default:
      return 'info';
  }
});

// 组件卸载时清理定时器
onUnmounted(() => {
  stopPolling();
});
</script>

<style lang="less" scoped>
.recharge-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.recharge-header {
  text-align: center;
  margin-bottom: 40px;
  
  h2 {
    font-size: 32px;
    color: #FFFFFF;
    margin-bottom: 10px;
  }
  
  p {
    color: #999;
    font-size: 14px;
  }
}

.amount-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.amount-card {
  background: linear-gradient(145deg, #1A1A1A, #2A2A2A);
  border: 2px solid #333;
  border-radius: 12px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    border-color: #5EFF00;
    box-shadow: 0 5px 20px rgba(94, 255, 0, 0.3);
  }
  
  &.active {
    border-color: #5EFF00;
    background: linear-gradient(145deg, #2A2A2A, #1A1A1A);
    box-shadow: 0 5px 20px rgba(94, 255, 0, 0.4);
  }
  
  .amount-value {
    font-size: 36px;
    color: #5EFF00;
    font-weight: bold;
    margin-bottom: 10px;
  }
  
  .amount-bonus {
    color: #FF9800;
    font-size: 16px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    .bonus-tag {
      background: linear-gradient(135deg, #FF6B6B, #FF9800);
      color: white;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
    }
  }
  
  .amount-label {
    color: #999;
    font-size: 14px;
  }
}

.custom-amount-section {
  background: #1A1A1A;
  border-radius: 12px;
  padding: 30px;
  margin-top: 30px;
  
  h3 {
    color: #FFFFFF;
    font-size: 20px;
    margin-bottom: 20px;
  }
  
  .custom-input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
    
    .currency-symbol {
      font-size: 24px;
      color: #5EFF00;
      font-weight: bold;
    }
    
    :deep(.el-input-number) {
      width: 100%;
      
      .el-input__inner {
        background: #2A2A2A;
        border-color: #333;
        color: #FFFFFF;
      }
    }
    
    .amount-tip {
      color: #999;
      font-size: 12px;
    }
  }
  
  .custom-confirm-btn {
    width: 100%;
    height: 50px;
    font-size: 18px;
    font-weight: bold;
  }
}

.payment-content {
  padding: 20px 0;
}

.order-info {
  background: #f5f7fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  
  .info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .label {
      color: #666;
      font-weight: normal;
    }
    
    .value {
      color: #333;
      font-weight: bold;
      
      &.amount-highlight {
        color: #5EFF00;
        font-size: 20px;
      }
    }
  }
}

.qr-code-section {
  text-align: center;
  margin: 30px 0;
  
  .qr-code-image {
    width: 200px;
    height: 200px;
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 10px;
    background: white;
  }
  
  .qr-tip {
    margin-top: 15px;
    color: #666;
    font-size: 14px;
  }
}

.payment-status {
  margin: 20px 0;
}

.payment-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 25px;
}

.success-content {
  text-align: center;
  padding: 20px;
  
  .success-icon {
    width: 80px;
    height: 80px;
    line-height: 80px;
    background: #5EFF00;
    color: white;
    border-radius: 50%;
    font-size: 48px;
    margin: 0 auto 20px;
    font-weight: bold;
  }
  
  h3 {
    color: #333;
    font-size: 24px;
    margin-bottom: 15px;
  }
  
  .success-detail {
    color: #666;
    font-size: 16px;
    margin: 10px 0;
  }
  
  .success-time {
    color: #999;
    font-size: 14px;
    margin-top: 15px;
  }
}
</style>
